import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./lib/api";
import enDict from "./i18n/en.json";
import ptDict from "./i18n/pt.json";
import esDict from "./i18n/es.json";
import frDict from "./i18n/fr.json";

const DICTS = { en: enDict, pt: ptDict, es: esDict, fr: frDict };
const detectLang = () => { const l = (navigator.language || "en").toLowerCase(); return ["pt", "es", "fr", "en"].find((x) => l.startsWith(x)) || "en"; };
const detectCurrency = () => "AUD";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const CURRENCY_SYMBOL = { BRL: "R$", AUD: "A$", USD: "US$", GBP: "£", EUR: "€", CAD: "C$" };
const CURRENCY_LOCALE = { BRL: "pt-BR", AUD: "en-AU", USD: "en-US", GBP: "en-GB", EUR: "de-DE", CAD: "en-CA" };
const REGION_CURRENCY = { BR: "BRL", AU: "AUD", US: "USD", GB: "GBP", CA: "CAD", DE: "EUR", FR: "EUR", ES: "EUR", IT: "EUR", NL: "EUR", IE: "EUR", PT: "EUR" };

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [lang, setLangState] = useState(localStorage.getItem("lang") || detectLang());
  const [currency, setCurrencyState] = useState(localStorage.getItem("currency") || detectCurrency());
  const [dict, setDict] = useState({});
  const [rates, setRates] = useState({ USD: 1, BRL: 5.35, AUD: 1.52 });
  const [favorites, setFavorites] = useState(new Set());

  const setLang = (l) => { localStorage.setItem("lang", l); setLangState(l); if (user) api.put("/profile", { preferred_language: l }).catch(() => {}); };
  const setCurrency = (c) => { localStorage.setItem("currency", c); setCurrencyState(c); };

  const t = useCallback((key) => (dict[key] || key), [dict]);

  const loadDict = useCallback((l) => { setDict(DICTS[l] || DICTS.en); }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) { return; }
    try {
      const { data } = await api.get("/auth/me"); setUser(data);
      if (data.preferred_language && !localStorage.getItem("lang")) setLangState(data.preferred_language);
    }
    catch { setUser(false); }
  }, []);

  useEffect(() => { loadDict(lang); }, [lang, loadDict]);
  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => {
    api.get("/currency/rates").then(({ data }) => setRates(data.rates)).catch(() => {});
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user); return data.user;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user); return data.user;
  };
  const logout = async () => { try { await api.post("/auth/logout"); } catch {} setUser(false); };
  const refreshUser = async () => { try { const { data } = await api.get("/auth/me"); setUser(data); } catch {} };

  // convert an amount given in fromCurrency into the active display currency
  const convert = (amount, fromCurrency) => {
    const inUsd = amount / (rates[fromCurrency] || 1);
    return inUsd * (rates[currency] || 1);
  };
  const formatMoney = (amount, cur) => {
    try {
      return new Intl.NumberFormat(CURRENCY_LOCALE[cur] || "en-US",
        { style: "currency", currency: cur }).format(amount);
    } catch {
      return `${CURRENCY_SYMBOL[cur] || ""} ${amount.toFixed(2)}`;
    }
  };
  const priceDisplay = (amount, fromCurrency) => formatMoney(convert(amount, fromCurrency), currency);

  const loadFavorites = useCallback(async () => {
    try { const { data } = await api.get("/favorites/ids"); setFavorites(new Set(data)); } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (user) loadFavorites(); else setFavorites(new Set()); }, [user, loadFavorites]);
  const toggleFavorite = async (lid) => {
    try {
      const { data } = await api.post(`/favorites/${lid}`);
      setFavorites((prev) => { const n = new Set(prev); if (data.favorited) n.add(lid); else n.delete(lid); return n; });
      return data.favorited;
    } catch { return null; }
  };

  return (
    <AppContext.Provider value={{
      user, setUser, login, register, logout, refreshUser,
      lang, setLang, currency, setCurrency, t, rates,
      formatMoney, convert, priceDisplay,
      favorites, toggleFavorite,
    }}>
      {children}
    </AppContext.Provider>
  );
}
